import { useCallback, useEffect, useState } from "react";
import { useAuthContext } from "../../../../context/AuthContext";
import getRecommendationHistoryByResource from "../../../../apis/getRecommendationHistoryByResource";
import removeRecommendation from "../../../../apis/removeRecommendation";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const RecommendHistory = ({ resourceSlug }) => {
  const { userDetails } = useAuthContext();
  const { user_slug, user_primary_role } = JSON.parse(userDetails);

  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");

  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [remark, setRemark] = useState("");
  const [infoData, setInfoData] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleInfoClick = (data) => {
    setInfoData(data);
    setShowInfoModal(true);
  };

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRecommendationHistoryByResource(
        resourceSlug,
        user_slug
      );
      if (res?.data?.status === 1 && Array.isArray(res.data.recommendations)) {
        setRecommendations(res.data.recommendations);
      } else {
        setRecommendations([]);
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  }, [resourceSlug, user_slug]);

  useEffect(() => {
    if (user_slug && user_primary_role === "2") {
      fetchRecommendations();
    }
  }, [fetchRecommendations, user_slug, user_primary_role]);

  const truncateText = (text, limit = 30) =>
    text.length <= limit ? text : `${text.slice(0, limit)}...`;

  const handleReadMore = (title, fullText) => {
    setModalTitle(title);
    setModalContent(fullText);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalTitle("");
    setModalContent("");
  };

  const openRemarkModal = (recommendationId) => {
    setSelectedRecommendation(recommendationId);
    setRemark("");
    setShowRemarkModal(true);
  };

  const submitRemoval = async () => {
    try {
      const res = await removeRecommendation(
        resourceSlug,
        user_slug,
        selectedRecommendation,
        remark // include this if your API accepts remarks
      );
      if (res?.data?.status === 1) {
        setRecommendations((prev) =>
          prev.map((rec) =>
            rec.recommendation_id === selectedRecommendation
              ? {
                  ...rec,
                  status: 0,
                  removed_on: res.data.data.removed_on,
                }
              : rec
          )
        );
        setShowRemarkModal(false);
        Swal.fire("Deleted!", res?.data?.message, "success");
      } else {
        Swal.fire("Oops!", res?.data?.message, "error");
      }
    } catch (error) {
      console.error("Unable to remove recommendation:", error);
    }
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body">
        <div className="d-block mb-2">
          <h2>Network Recommendations</h2>
          <span className="text-sm text-muted d-block">
            Candidates hand-selected by your network
          </span>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : recommendations.length === 0 ? (
          <div className="alert alert-warning d-flex align-items-center">
            <div className="ms-2">No recommendations available.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table table-responsive">
              <thead>
                <tr>
                  <th>Date Sent</th>
                  <th>Client</th>
                  <th>Selected by</th>
                  <th>Subject</th>
                  <th>Content</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map((rec) => (
                  <tr key={rec.recommendation_id}>
                    <td>{rec.recommended_at}</td>
                    <td>{rec.client_name}</td>
                    <td>{rec.resource_manager_name}</td>
                    <td>
                      {rec.subject.length > 20 ? (
                        <>
                          <div>{truncateText(rec.subject)}</div>
                          <button
                            className="btn btn-link btn-sm p-0"
                            onClick={() =>
                              handleReadMore("Subject", rec.subject)
                            }
                          >
                            Read more
                          </button>
                        </>
                      ) : (
                        <span className="text-dark">{rec.subject}</span>
                      )}
                    </td>
                    <td>
                      {rec.content.length > 30 ? (
                        <>
                          <div>{truncateText(rec.content)}</div>
                          <button
                            className="btn btn-link btn-sm p-0"
                            onClick={() =>
                              handleReadMore("Content", rec.content)
                            }
                          >
                            Read more
                          </button>
                        </>
                      ) : (
                        <span className="text-dark">{rec.content}</span>
                      )}
                    </td>
                    <td className="text-end">
                      {rec.status === "1" ? (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => openRemarkModal(rec.recommendation_id)}
                        >
                          Remove
                        </button>
                      ) : (
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-info"
                          style={{ cursor: "pointer", fontSize: "1.3rem" }}
                          onClick={() =>
                            handleInfoClick({
                              removed_on: rec.removed_on,
                              removed_by: rec.removed_by_name || "Unknown",
                              reason: rec.remove_remark || "Not specified",
                            })
                          }
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showInfoModal && infoData && (
          <div
            className="modal show fade"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            onClick={() => setShowInfoModal(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header bg-light border-bottom-0">
                  <h2>Removal Details</h2>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowInfoModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="fw-semibold text-muted text-sm">
                      Removed On:
                    </label>
                    <div className="text-success text-sm">
                      {infoData.removed_on}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-semibold text-muted text-sm">
                      Removed By:
                    </label>
                    <div className="text-link text-sm">
                      {infoData.removed_by}
                    </div>
                  </div>
                  <div className="mb-1">
                    <label className="fw-semibold text-muted text-sm">
                      Reason:
                    </label>
                    <div className="text-danger">
                      {infoData.reason || (
                        <span className="text-muted fst-italic">
                          No reason provided
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-top-0">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowInfoModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Read More Modal */}
        {showModal && (
          <div
            className="modal show fade"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            onClick={closeModal}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Full {modalTitle}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>{modalContent}</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remark Input Modal */}
        {showRemarkModal && (
          <div
            className="modal show fade"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
            role="dialog"
            onClick={() => setShowRemarkModal(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Remark for Removal</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowRemarkModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    placeholder="Enter your remark..."
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    rows={4}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowRemarkModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={submitRemoval}
                    disabled={!remark.trim()}
                  >
                    Confirm Removal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendHistory;
